import LFCAutocomplete from '_components/inputs/LFCAutocomplete';
import ProgressBar from '_components/ProgressBar';
import {
  CommitInfo,
  getSourceCollectors,
  getSourceCommits,
  getSourceContent,
  getSourceFiles,
  SourceFile
} from '_logics/LFCSourceUtil';
import GenericTemplate from '_templates/GenericTemplate';
import {useSnackbar} from 'notistack';
import {useCallback, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import MapIcon from '@mui/icons-material/Map';
import {Alert, Box, Button, Card, Divider} from '@mui/material';
import LFCButton from '_components/inputs/LFCButton';

import FileHistoryDialog from './_components/FileHistoryDialog';
import FileTree from './_components/FileTree';
import FloorMapDialog from './_components/FloorMapDialog';
import SourceViewer from './_components/SourceViewer';

const PghistoryPage = () => {
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const [startProcess, setStartProcess] = useState(false);
  const [contentLoading, setContentLoading] = useState(false);

  // 選択状態
  const [selectedDevice, setSelectedDevice] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<SourceFile | null>(null);

  // マスタデータ
  const [deviceOptions, setDeviceOptions] = useState<{label: string}[]>([]);
  const [autoCompleteReset, setAutoCompleteReset] = useState(false);

  // ファイル一覧
  const [files, setFiles] = useState<SourceFile[]>([]);

  // 選択ファイルの変更履歴
  const [fileHistory, setFileHistory] = useState<CommitInfo[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [nextCommitId, setNextCommitId] = useState<string | null>(null);
  const [loadMoreLoading, setLoadMoreLoading] = useState(false);
  // ファイル履歴ダイアログ
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);

  // フロアマップダイアログ
  const [floorMapOpen, setFloorMapOpen] = useState(false);

  // ファイル内容
  const [content, setContent] = useState<string | null>(null);
  const [fileSize, setFileSize] = useState<number | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [isBinary, setIsBinary] = useState(false);
  const [latestCommitDate, setLatestCommitDate] = useState<string | null>(null);

  // リサイズ機能
  const [leftPanelWidth, setLeftPanelWidth] = useState(300);
  const containerRef = useRef<HTMLDivElement>(null);
  const isResizing = useRef(false);

  // ファイル内容取得のAbortController
  const contentAbortControllerRef = useRef<AbortController | null>(null);

  const handleMouseDown = useCallback(() => {
    isResizing.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isResizing.current || !containerRef.current) return;
    const containerRect = containerRef.current.getBoundingClientRect();
    const newWidth = e.clientX - containerRect.left;
    // 最小150px、最大50%
    const maxWidth = containerRect.width * 0.5;
    setLeftPanelWidth(Math.max(150, Math.min(newWidth, maxWidth)));
  }, []);

  const handleMouseUp = useCallback(() => {
    isResizing.current = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  /**
   * 初期処理: collector一覧取得
   */
  useEffect(() => {
    const fetchCollectors = async () => {
      setStartProcess(true);
      try {
        const result = await getSourceCollectors();
        setDeviceOptions(result.map(name => ({label: name})));
      } catch (error) {
        console.error('端末一覧取得エラー:', error);
        enqueueSnackbar('端末一覧の取得に失敗しました', {variant: 'error'});
      } finally {
        setStartProcess(false);
      }
    };

    fetchCollectors();
  }, []);

  /**
   * 端末選択時: ファイル一覧を取得
   */
  const handleDeviceChange = async (relayDatas: {name: string; data: string | string[]}) => {
    const deviceName = Array.isArray(relayDatas.data) ? relayDatas.data[0] : relayDatas.data;
    setSelectedDevice(deviceName);
    setSelectedFile(null);
    setFiles([]);
    setContent(null);

    if (!deviceName) {
      return;
    }

    setStartProcess(true);
    try {
      const filesResult = await getSourceFiles(deviceName);
      setFiles(filesResult.files);

      if (filesResult.files.length === 0) {
        enqueueSnackbar('ファイルがありません', {variant: 'info'});
      }
    } catch (error) {
      console.error('データ取得エラー:', error);
      enqueueSnackbar('データの取得に失敗しました', {variant: 'error'});
    } finally {
      setStartProcess(false);
    }
  };

  /**
   * ファイル選択（ツリーからファイルが選択された場合）
   */
  const handleFileSelect = async (file: SourceFile) => {
    // フォルダの場合はツリー内で展開されるので何もしない
    if (file.isFolder) {
      return;
    }

    // 前のリクエストをキャンセル
    if (contentAbortControllerRef.current) {
      contentAbortControllerRef.current.abort();
    }

    // 新しいAbortControllerを作成
    const abortController = new AbortController();
    contentAbortControllerRef.current = abortController;

    // ファイルの場合は内容を取得
    setSelectedFile(file);
    setContentLoading(true);
    setContent(null);
    setIsBinary(false);
    setFileHistory([]);
    setLatestCommitDate(null);

    try {
      // ファイル内容と最新コミット情報を並行取得
      const relativePath = file.path.replace(`devices/${selectedDevice}/`, '');
      const [contentResult, historyResult] = await Promise.all([
        getSourceContent(file.path, undefined, abortController.signal),
        getSourceCommits(selectedDevice, relativePath, 1).catch(() => ({
          commits: [],
          nextCommitId: null
        }))
      ]);

      // 最新コミット日時を設定
      if (historyResult.commits.length > 0) {
        setLatestCommitDate(historyResult.commits[0].date);
      }

      // large-response の場合
      if (contentResult.type === 'large-response') {
        // 大容量ファイルはダウンロードURLのみ表示
        setDownloadUrl(contentResult.url || null);
        setFileSize(contentResult.size);
        setContent(null);
        enqueueSnackbar('大容量ファイルのためダウンロードが必要です', {variant: 'info'});
      } else {
        setContent(contentResult.content || '');
        setFileSize(contentResult.size);
        setIsBinary(contentResult.isBinary || false);
        setDownloadUrl(null);
      }
    } catch (error) {
      // キャンセルされた場合は何もしない
      if (error instanceof Error && error.name === 'CanceledError') {
        return;
      }
      console.error('ファイル内容取得エラー:', error);
      enqueueSnackbar('ファイル内容の取得に失敗しました', {variant: 'error'});
    } finally {
      // このリクエストがまだアクティブな場合のみローディングを解除
      if (contentAbortControllerRef.current === abortController) {
        setContentLoading(false);
      }
    }
  };

  /**
   * 変更履歴を開く（Lazy loading）
   */
  const handleOpenHistory = async () => {
    if (!selectedFile) return;

    setHistoryDialogOpen(true);
    setFileHistory([]);
    setNextCommitId(null);
    setHistoryLoading(true);

    try {
      const relativePath = selectedFile.path.replace(`devices/${selectedDevice}/`, '');
      const historyResult = await getSourceCommits(selectedDevice, relativePath, 10);
      setFileHistory(historyResult.commits);
      setNextCommitId(historyResult.nextCommitId);
    } catch (error) {
      console.error('ファイル履歴取得エラー:', error);
      enqueueSnackbar('ファイル履歴の取得に失敗しました', {variant: 'error'});
    } finally {
      setHistoryLoading(false);
    }
  };

  /**
   * さらに履歴を読み込む
   */
  const handleLoadMoreHistory = async () => {
    if (!selectedFile || !nextCommitId) return;

    setLoadMoreLoading(true);

    try {
      const relativePath = selectedFile.path.replace(`devices/${selectedDevice}/`, '');
      const historyResult = await getSourceCommits(selectedDevice, relativePath, 10, nextCommitId);
      setFileHistory(prev => [...prev, ...historyResult.commits]);
      setNextCommitId(historyResult.nextCommitId);
    } catch (error) {
      console.error('ファイル履歴取得エラー:', error);
      enqueueSnackbar('追加の履歴取得に失敗しました', {variant: 'error'});
    } finally {
      setLoadMoreLoading(false);
    }
  };

  return (
    <GenericTemplate title="Source Management">
      <ProgressBar startProcess={startProcess} />

      {/* 上部: 端末選択 */}
      <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
        <LFCAutocomplete
          name="device"
          label={t('端末')}
          id="device"
          size="small"
          style={{minWidth: 400}}
          value={selectedDevice ? {label: selectedDevice} : null}
          onChange={handleDeviceChange}
          onReset={autoCompleteReset}
          doneReset={setAutoCompleteReset}
          multiple={false}
          selectItem={deviceOptions}
        />
        <LFCButton onClick={() => setFloorMapOpen(true)}>
          <MapIcon fontSize="small" sx={{mr: 0.5}} />
          {t('マップ')}
        </LFCButton>
      </Box>

      <Divider sx={{margin: '0.5em 0'}} />

      {/* メインエリア */}
      <Box sx={{height: 'calc(100vh - 160px)', display: 'flex', flexDirection: 'column'}}>
        {!selectedDevice ? (
          <Alert severity="info">端末を選択してください</Alert>
        ) : (
          <Box ref={containerRef} sx={{display: 'flex', height: '100%', overflow: 'hidden'}}>
            {/* ファイル一覧 */}
            <Box sx={{width: leftPanelWidth, flexShrink: 0, height: '100%', overflow: 'hidden'}}>
              <Card
                variant="outlined"
                sx={{
                  p: 1,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden'
                }}
              >
                <FileTree
                  files={files}
                  selectedPath={selectedFile?.path || null}
                  onSelect={handleFileSelect}
                  deviceName={selectedDevice}
                />
              </Card>
            </Box>

            {/* リサイズハンドル */}
            <Box
              onMouseDown={handleMouseDown}
              sx={{
                width: 12,
                mx: 0.5,
                cursor: 'col-resize',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover > div': {bgcolor: 'primary.main'}
              }}
            >
              <Box
                sx={{
                  width: 4,
                  height: 40,
                  bgcolor: 'divider',
                  borderRadius: 1
                }}
              />
            </Box>

            {/* ファイルビューア */}
            <Box sx={{flex: 1, minWidth: 0, overflow: 'hidden'}}>
              {isBinary ? (
                <Alert severity="info">
                  バイナリファイルはプレビューできません
                  {downloadUrl && (
                    <Button href={downloadUrl} target="_blank" sx={{ml: 2}}>
                      ダウンロード
                    </Button>
                  )}
                </Alert>
              ) : downloadUrl && !content ? (
                <Alert severity="info">
                  大容量ファイルです
                  <Button href={downloadUrl} target="_blank" sx={{ml: 2}}>
                    ダウンロード
                  </Button>
                </Alert>
              ) : (
                <SourceViewer
                  content={content}
                  fileName={selectedFile?.name || null}
                  filePath={selectedFile?.path || null}
                  fileSize={fileSize}
                  downloadUrl={downloadUrl}
                  loading={contentLoading}
                  hasHistory={selectedFile !== null}
                  onOpenHistory={handleOpenHistory}
                  commitDate={latestCommitDate}
                />
              )}
            </Box>
          </Box>
        )}
      </Box>

      {/* ファイル変更履歴ダイアログ */}
      <FileHistoryDialog
        open={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
        filePath={selectedFile?.path || ''}
        fileHistory={fileHistory}
        historyLoading={historyLoading}
        hasMore={nextCommitId !== null}
        onLoadMore={handleLoadMoreHistory}
        loadMoreLoading={loadMoreLoading}
      />

      {/* フロアマップダイアログ */}
      <FloorMapDialog
        open={floorMapOpen}
        onClose={() => setFloorMapOpen(false)}
        onSelectDevice={(deviceName: string) => {
          handleDeviceChange({name: 'device', data: deviceName});
        }}
      />
    </GenericTemplate>
  );
};

export default PghistoryPage;
